import { FC } from 'react';
import EmptyState from '@/components/seop/EmptyState';
import type { SeopScenarioRow, SeopSegmentTotal } from '@/types/seop';
import { fmtMarginPct, fmtMoneyBRL, fmtUnits, seopNum } from '@/utils/seopFormat';
import { alternarOrdenacao, FILTRO_INICIAL, type ChaveDeOrdenacao, type FiltroDoCenario } from '@/utils/seopCenario';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const NUM = `${CELL} text-right tabular-nums`;

/** As colunas da tabela econômica, na ordem em que aparecem. `chave` é o que ordena. */
const COLUNAS: { chave: ChaveDeOrdenacao; rotulo: string; numerica: boolean }[] = [
  { chave: 'code', rotulo: 'Código', numerica: false },
  { chave: 'description', rotulo: 'Produto', numerica: false },
  { chave: 'segment', rotulo: 'Linha', numerica: false },
  { chave: 'net_qty', rotulo: 'Quantidade', numerica: true },
  { chave: 'unit_value', rotulo: 'PMV', numerica: true },
  { chave: 'cpv_unit_value', rotulo: 'CPV', numerica: true },
  { chave: 'margin_percent', rotulo: '% Margem', numerica: true },
  { chave: 'unit_margin_value', rotulo: 'R$ unit. margem', numerica: true },
  { chave: 'final_value', rotulo: 'R$ PMV geral', numerica: true },
  { chave: 'contribution_value', rotulo: 'R$ margem geral', numerica: true },
  { chave: 'difference', rotulo: 'Diferença', numerica: true },
];

const CabecalhoOrdenavel: FC<{
  coluna: { chave: ChaveDeOrdenacao; rotulo: string; numerica: boolean };
  filtro: FiltroDoCenario;
  onOrdenar: (chave: ChaveDeOrdenacao) => void;
}> = ({ coluna, filtro, onOrdenar }) => {
  const ativa = filtro.ordenarPor === coluna.chave;
  const icone = ativa ? (filtro.direcao === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'swap_vert';
  return (
    <th
      className={`pb-2 pr-3 ${coluna.numerica ? 'text-right' : 'text-left'}`}
      aria-sort={ativa ? (filtro.direcao === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onOrdenar(coluna.chave)}
        className={`flex items-center gap-1 uppercase tracking-wide hover:text-content-primary ${
          coluna.numerica ? 'ml-auto' : ''
        } ${ativa ? 'text-accent' : ''}`}
      >
        <span>{coluna.rotulo}</span>
        <span className={`material-symbols-outlined text-sm ${ativa ? 'text-accent' : 'text-content-muted/50'}`}>
          {icone}
        </span>
      </button>
    </th>
  );
};

/** Busca, linha de produção e o aviso de quanto ficou de fora. */
const FiltrosDoCenario: FC<{
  filtro: FiltroDoCenario;
  linhas: string[];
  visiveis: number;
  total: number;
  onFiltro: (f: FiltroDoCenario) => void;
}> = ({ filtro, linhas, visiveis, total, onFiltro }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2">
    <input
      aria-label="Buscar item, descrição ou linha"
      placeholder="Buscar item, descrição ou linha"
      value={filtro.busca}
      onChange={(e) => onFiltro({ ...filtro, busca: e.target.value })}
      className="w-72 rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm"
    />
    <select
      aria-label="Linha de produção"
      value={filtro.linha}
      onChange={(e) => onFiltro({ ...filtro, linha: e.target.value })}
      className="rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm"
    >
      <option value="">Todas as linhas</option>
      {linhas.map((l) => (
        <option key={l} value={l}>
          {l}
        </option>
      ))}
    </select>
    {(filtro.busca || filtro.linha || filtro.ordenarPor) && (
      <button
        type="button"
        data-testid="seop-limpar-filtro"
        onClick={() => onFiltro(FILTRO_INICIAL)}
        className="rounded-lg border border-theme-border px-3 py-1.5 text-xs text-content-secondary hover:text-content-primary"
      >
        Limpar
      </button>
    )}
    {visiveis !== total && (
      <span data-testid="seop-contagem-filtro" className="text-xs text-content-muted">
        Mostrando {fmtUnits(visiveis)} de {fmtUnits(total)} itens — os cartões e o "Total exibido" somam só o que
        aparece; os totais por linha (SPPV, SPGV) são do período inteiro.
      </span>
    )}
  </div>
);

/**
 * Uma linha do rodapé, alinhada às colunas do corpo: quantidade embaixo de Quantidade,
 * % embaixo de % Margem e, embaixo de Diferença, a mesma conta das linhas (PMV geral −
 * margem geral). Antes só havia R$, e a % ficava na coluna da Diferença.
 */
const LinhaDeTotal: FC<{ rotulo: string; total: SeopSegmentTotal; destaque?: boolean; testId?: string }> = ({
  rotulo,
  total,
  destaque = false,
  testId,
}) => {
  const tom = destaque ? 'border-t border-theme-border text-sm font-semibold text-content-primary' : 'text-xs text-content-muted';
  const num = 'py-1.5 pr-3 text-right tabular-nums';
  return (
    <tr data-testid={testId} className={tom}>
      <td className="py-1.5 pr-3" colSpan={3}>
        {rotulo}
      </td>
      <td className={num}>{fmtUnits(seopNum(total.net_qty))}</td>
      <td colSpan={2} />
      <td className={num}>{fmtMarginPct(seopNum(total.margin_rate))}</td>
      <td />
      <td className={num}>{fmtMoneyBRL(seopNum(total.final_value))}</td>
      <td className={num}>{fmtMoneyBRL(seopNum(total.contribution_value))}</td>
      <td className={num}>{fmtMoneyBRL(seopNum(total.final_value) - seopNum(total.contribution_value))}</td>
    </tr>
  );
};

const CorpoDaTabela: FC<{ linhas: SeopScenarioRow[] }> = ({ linhas }) => (
  <tbody>
    {linhas.map((r) => (
      <tr key={r.code} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
        <td className={CELL}>{r.code}</td>
        <td className={`${CELL} max-w-xs truncate`} title={r.description}>
          {r.description}
        </td>
        <td className={CELL}>{r.segment}</td>
        <td className={`${NUM} font-semibold`} title={`Origem: ${r.source}`}>
          {fmtUnits(seopNum(r.net_qty))}
        </td>
        <td className={NUM}>{fmtMoneyBRL(seopNum(r.unit_value))}</td>
        <td className={NUM}>{fmtMoneyBRL(seopNum(r.cpv_unit_value))}</td>
        <td className={NUM}>{fmtMarginPct(seopNum(r.margin_percent))}</td>
        <td className={NUM}>{fmtMoneyBRL(seopNum(r.unit_margin_value))}</td>
        <td className={`${NUM} font-semibold`}>{fmtMoneyBRL(seopNum(r.final_value))}</td>
        <td className={`${NUM} font-semibold`}>{fmtMoneyBRL(seopNum(r.contribution_value))}</td>
        <td className={NUM}>{fmtMoneyBRL(seopNum(r.difference))}</td>
      </tr>
    ))}
    {linhas.length === 0 && (
      <tr>
        <td className={`${CELL} text-content-muted`} colSpan={COLUNAS.length}>
          Nenhum item casa com o filtro.
        </td>
      </tr>
    )}
  </tbody>
);

interface TabelaEconomicaProps {
  titulo: string;
  linhas: SeopScenarioRow[];
  totalDeLinhas: number;
  /** A soma do que está em tela: o período inteiro sem filtro, o recorte com filtro. */
  totalExibido: SeopSegmentTotal;
  /** Totais por linha de produção — sempre do período inteiro, calculados pelo motor. */
  totais: SeopSegmentTotal[];
  filtro: FiltroDoCenario;
  linhasDeProducaoDisponiveis: string[];
  onFiltro: (f: FiltroDoCenario) => void;
}

/** A tabela econômica do período: plano atual ou um cenário salvo aberto para leitura. */
const TabelaEconomica: FC<TabelaEconomicaProps> = ({
  titulo,
  linhas,
  totalDeLinhas,
  totalExibido,
  totais,
  filtro,
  linhasDeProducaoDisponiveis,
  onFiltro,
}) => {
  const recortado = linhas.length !== totalDeLinhas;
  const rotuloDoTotal = recortado
    ? `Total exibido (${fmtUnits(linhas.length)} de ${fmtUnits(totalDeLinhas)} itens)`
    : 'Total do período';
  return (
    <div className="rounded-xl border border-theme-border bg-surface-card p-5">
      <h3 className="mb-3 font-semibold text-content-primary">{titulo}</h3>
      {totalDeLinhas === 0 ? (
        <EmptyState
          icon="insights"
          title="Nenhum produto com demanda no período"
          hint="Lance a demanda do mês na aba Demanda para a tabela econômica aparecer."
        />
      ) : (
        <>
          <FiltrosDoCenario
            filtro={filtro}
            linhas={linhasDeProducaoDisponiveis}
            visiveis={linhas.length}
            total={totalDeLinhas}
            onFiltro={onFiltro}
          />
          <div className="overflow-x-auto">
            <table data-testid="seop-tabela-economica" className="w-full">
              <thead>
                <tr className="border-b border-theme-border text-xs text-content-muted">
                  {COLUNAS.map((c) => (
                    <CabecalhoOrdenavel
                      key={c.chave}
                      coluna={c}
                      filtro={filtro}
                      onOrdenar={(chave) => onFiltro(alternarOrdenacao(filtro, chave))}
                    />
                  ))}
                </tr>
              </thead>
              <CorpoDaTabela linhas={linhas} />
              <tfoot>
                <LinhaDeTotal testId="seop-total-exibido" rotulo={rotuloDoTotal} total={totalExibido} destaque />
                {totais.map((t) => (
                  <LinhaDeTotal key={t.segment} rotulo={`Total ${t.segment}`} total={t} />
                ))}
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TabelaEconomica;
